import type { TFunction } from "i18next";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Text from "components/ui/Text";
import { renewalPeriodOfDays, type PricingModelItem } from "services/pricingModel/model";
import { periodToString } from "services/pricingModel/utils";
import { formatMoney } from "utils/money";
import styles from "./styles.module.scss";

function FootNote({ pricingItem }: { pricingItem: PricingModelItem }) {
  const { t } = useTranslation();
  const price = formatMoney(pricingItem.price, pricingItem.currency);

  if (pricingItem.purchase_type === "subscription") {
    return (
      <p className={styles.legalWarning}>
        {t("Common.checkout.subscriptionLegalNotice", {
          dueValue: `${price}/${periodToString(t, pricingItem.length)}`,
          billingFrequency: billingFrequency(t, pricingItem.length),
        })}
      </p>
    );
  }

  return (
    <p className={styles.legalWarning}>
      {t("Common.checkout.oneOffLegalNotice", { dueValue: price })}
    </p>
  );
}

function billingFrequency(t: TFunction, length: number | null) {
  switch (renewalPeriodOfDays(length)) {
    case "monthly":
      return t("Common.billingFrequency.month");
    case "annual":
      return t("Common.billingFrequency.year");
    case null:
      return length === null
        ? t("Common.billingFrequency.unknownPeriod")
        : t("Common.billingFrequency.days", { count: length });
  }
}

export default function CheckoutSummary({
  pricingItem,
  children,
}: {
  pricingItem: PricingModelItem;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const isSubscription = pricingItem.purchase_type === "subscription";
  return (
    <div className={styles.root}>
      <div className={styles.dueContainer}>
        <Text variant="subtitle-3">{t("Common.checkout.totalDue")}</Text>
        <span className={styles.dueValue}>
          {formatMoney(pricingItem.price, pricingItem.currency)}
        </span>
        {isSubscription && (
          <Text variant="subtitle-3" className={styles.duePeriod}>
            /{periodToString(t, pricingItem.length)}
          </Text>
        )}
      </div>
      {children}
      <FootNote pricingItem={pricingItem} />
    </div>
  );
}
